/**
 * PermitApproval — step-by-step Permit2 approval UI.
 *
 * Shows two approval steps with status indicators:
 * Step 1: ERC-20 → Permit2 (one-time per token)
 * Step 2: Permit2 → Universal Router (30-day TTL)
 *
 * Story 5.2 Task 4
 */

import { useEffect, useRef } from "react";

import { useApproveToken } from "../../hooks/chain/use-approve-token";
import { usePermit2Allowance } from "../../hooks/chain/use-permit2-allowance";

type PermitApprovalProps = {
	tokenAddress: `0x${string}`;
	walletAddress: string;
	amountIn: bigint;
	onApprovalComplete: () => void;
};

export function PermitApproval({
	tokenAddress,
	walletAddress,
	amountIn,
	onApprovalComplete,
}: PermitApprovalProps) {
	const { needsErc20Approval, needsPermit2Approval, isLoading } = usePermit2Allowance(
		tokenAddress,
		walletAddress,
		amountIn
	);

	const { approveErc20, approvePermit2, txState, reset } = useApproveToken(tokenAddress);

	const allApproved = !needsErc20Approval && !needsPermit2Approval && !isLoading;
	const calledRef = useRef(false);

	// Notify parent when all approvals complete (once per approval cycle)
	useEffect(() => {
		if (allApproved && !calledRef.current) {
			calledRef.current = true;
			onApprovalComplete();
		}
	}, [allApproved, onApprovalComplete]);

	// Reset the guard only when approval is actually needed again
	// (not during loading transitions, which would cause an infinite loop)
	useEffect(() => {
		if (needsErc20Approval || needsPermit2Approval) {
			calledRef.current = false;
		}
	}, [needsErc20Approval, needsPermit2Approval]);

	if (isLoading) {
		return (
			<div className="rounded-lg border border-pastel-border bg-white p-4">
				<p className="text-sm text-pastel-gray">Checking approvals...</p>
			</div>
		);
	}

	if (allApproved) {
		return (
			<div className="rounded-lg border border-green-200 bg-green-50 p-4">
				<div className="flex items-center gap-2">
					<span className="text-green-600">&#10003;</span>
					<p className="text-sm font-medium text-green-800">Ready to swap</p>
				</div>
			</div>
		);
	}

	const isTxPending =
		txState.status === "preparing" ||
		txState.status === "awaiting_signature" ||
		txState.status === "pending";

	return (
		<div className="rounded-lg border border-pastel-border bg-white p-4 space-y-3">
			<h4 className="text-sm font-semibold text-pastel-black">Token Approvals Required</h4>

			{/* Step 1: ERC-20 → Permit2 */}
			<div className="flex items-start gap-3">
				<div className="flex-shrink-0 mt-0.5">
					{needsErc20Approval ? (
						<div className="h-5 w-5 rounded-full border-2 border-pastel-gray" />
					) : (
						<div
							data-testid="step-1-complete"
							className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center"
						>
							<span className="text-white text-xs">&#10003;</span>
						</div>
					)}
				</div>
				<div className="flex-1">
					<p className="text-sm font-medium text-pastel-black">Approve token spending</p>
					<p className="text-xs text-pastel-gray">
						Allow Permit2 contract to manage your token approvals (one-time)
					</p>
					{needsErc20Approval && !isTxPending && (
						<button
							type="button"
							onClick={approveErc20}
							className="mt-2 rounded-lg bg-pastel-vibrant-blue px-4 py-1.5 text-xs font-semibold text-white hover:bg-pastel-vibrant-blue/90 transition-colors"
						>
							Approve
						</button>
					)}
				</div>
			</div>

			{/* Step 2: Permit2 → Universal Router */}
			<div className="flex items-start gap-3">
				<div className="flex-shrink-0 mt-0.5">
					{needsPermit2Approval ? (
						<div className="h-5 w-5 rounded-full border-2 border-pastel-gray" />
					) : (
						<div
							data-testid="step-2-complete"
							className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center"
						>
							<span className="text-white text-xs">&#10003;</span>
						</div>
					)}
				</div>
				<div className="flex-1">
					<p className="text-sm font-medium text-pastel-black">Authorize Universal Router</p>
					<p className="text-xs text-pastel-gray">
						Grant the swap router permission to trade on your behalf (30-day approval)
					</p>
					{needsPermit2Approval && !needsErc20Approval && !isTxPending && (
						<button
							type="button"
							onClick={approvePermit2}
							className="mt-2 rounded-lg bg-pastel-vibrant-blue px-4 py-1.5 text-xs font-semibold text-white hover:bg-pastel-vibrant-blue/90 transition-colors"
						>
							Authorize
						</button>
					)}
				</div>
			</div>

			{/* Transaction status */}
			{isTxPending && (
				<div className="flex items-center gap-2 pt-1">
					<div className="h-4 w-4 animate-spin rounded-full border-2 border-pastel-vibrant-blue border-t-transparent" />
					<p className="text-xs text-pastel-gray">
						{txState.status === "awaiting_signature"
							? "Confirm in wallet..."
							: "Waiting for confirmation..."}
					</p>
				</div>
			)}

			{/* Error state */}
			{txState.status === "failed" && txState.error && (
				<div className="rounded-md bg-red-50 p-2">
					<p className="text-xs text-red-700">{txState.error}</p>
					<button
						type="button"
						onClick={reset}
						className="mt-1 text-xs text-red-600 underline hover:no-underline"
					>
						Try again
					</button>
				</div>
			)}

			{txState.status === "rejected" && (
				<div className="rounded-md bg-yellow-50 p-2">
					<p className="text-xs text-yellow-700">Transaction cancelled.</p>
					<button
						type="button"
						onClick={reset}
						className="mt-1 text-xs text-yellow-600 underline hover:no-underline"
					>
						Try again
					</button>
				</div>
			)}
		</div>
	);
}
